import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  Stack,
  Grid,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material';

import Checkbox from '../checkbox/Checkbox';
import Icon from '../Icon';
import TextField from '../text-field/TextField';
import { notificationSuccess } from '../../store/notificationReducer';

function createHiddenAuthKey(key) {
  const lastSymbolsToShow = key.slice(key.length - 4);

  return '.......' + lastSymbolsToShow;
}

const notificationData = {
  isNotification: true,
  type: 'success',
  message: 'Copied to clipboard',
};

const AuthKeyCreatedDialog = ({ authKey, isDialogShow, onDialogAction }) => {
  const dispatch = useDispatch();
  const [isChecked, setCheckboxState] = useState(false);
  const [authKeys, setAuthKeys] = useState({
    default: '',
    base64: '',
  });

  // If we don't use useEffect, there can be a bug with empty auth key
  useEffect(() => {
    setAuthKeys({
      default: authKey,
      base64: btoa(authKey),
    });
  }, [authKey]);

  const createTxtFileHandler = () => {
    const defaultKey = `AuthKey: ${authKeys.default}`;
    const base64Key = `base64 key: ${authKeys.base64}`;

    const blob = new Blob([defaultKey, '\n', base64Key], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'authKey.txt';
    a.style.display = 'none';

    document.body.appendChild(a);
    a.click();

    // Clean the blob object and the anchor
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const checkboxStateHandler = () => {
    setCheckboxState((prevState) => !prevState);
  };

  const copyToClipboard = (_e, isBase64 = false) => {
    if (isBase64) {
      navigator.clipboard.writeText(authKeys.base64);

      dispatch(notificationSuccess(notificationData));

      return;
    }

    navigator.clipboard.writeText(authKeys.default);

    dispatch(notificationSuccess(notificationData));
  };

  return (
    <Dialog open={isDialogShow}>
      <Stack sx={{ gap: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" sx={{ textAlign: 'left' }}>
            Key Creation
          </Typography>
          <IconButton
            sx={{ p: 0 }}
            disabled={!isChecked}
            onClick={onDialogAction}
          >
            {/* TODO: find a way to change only colors of icons and not render different icons */}
            {isChecked ? (
              <Icon id="close-dialog" width={24} height={24} />
            ) : (
              <Icon id="close-dialog-disabled" width={24} height={24} />
            )}
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            padding: '12px',
            bgcolor: '#fff9eb',
            border: '1px solid  #ffd480',
            borderRadius: '10px',
            flex: '1 1 auto',
            textAlign: 'left',
          }}
        >
          <Icon
            id="warning"
            width="24"
            height="24"
            style={{ flex: '1 0 24px' }}
          />
          <Typography
            variant="body2"
            component="span"
            sx={{ letterSpacing: 0 }}
          >
            The key is shown only once. Save it in advance. After closing this
            window, the key will no longer be available.
          </Typography>
        </Box>
        <Stack gap={2}>
          <TextField
            id="auth-key"
            label="AuthKey for Windows/macOS Agents"
            value={createHiddenAuthKey(authKeys.default)}
            disabled
            helperText="Used to deploy Windows and macOS agents. Suitable for distribution via Active Directory or other device management solutions."
            icon={
              <IconButton onClick={copyToClipboard}>
                <Icon id="copy" width={20} height={20} />
              </IconButton>
            }
          />
          <TextField
            id="auth-key"
            label="AuthKey for iOS Agents (Base64)"
            value={createHiddenAuthKey(authKeys.base64)}
            disabled
            helperText="Used only for iOS agents. Distributed through MDM systems."
            icon={
              <IconButton onClick={(e) => copyToClipboard(e, true)}>
                <Icon id="copy" width={20} height={20} />
              </IconButton>
            }
          />
        </Stack>

        <Checkbox
          label={
            <Typography variant="body2">
              I’ve securely stored this key
            </Typography>
          }
          checked={isChecked}
          onChange={checkboxStateHandler}
          sx={{
            '.MuiCheckbox-root': {
              pt: 0,
              pb: 0,
              pl: 0,
            },
          }}
        />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Button
              variant="outlined"
              size="small"
              onClick={createTxtFileHandler}
              sx={{ width: '100%' }}
            >
              Download .txt
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              size="small"
              onClick={onDialogAction}
              disabled={!isChecked}
              sx={{
                width: '100%',
                // TODO: This is a bad way to apply disabled state,
                // find a way to make it generic alongside with error buttons disabled state
                '&:disabled': {
                  backgroundColor: 'primary.light',
                  color: '#fff',
                },
              }}
            >
              Done
            </Button>
          </Grid>
        </Grid>
      </Stack>
    </Dialog>
  );
};

export default AuthKeyCreatedDialog;
